const ensureTrailingSlash = (value) => {
  if (typeof value !== 'string') {
    return '';
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }

  return trimmed.endsWith('/') ? trimmed : `${trimmed}/`;
};

const resolveBaseUrl = () => {
  const envBase = typeof process.env.REACT_APP_PUBLIC_API_URL === 'string'
    ? process.env.REACT_APP_PUBLIC_API_URL.trim()
    : '';

  if (envBase) {
    return ensureTrailingSlash(envBase);
  }

  console.error("Env variable NOT FOUND");
};

const BASE_URL = resolveBaseUrl();

const AUTH_TOKEN_STORAGE_KEY = 'fourlink-auth-token';

const getStoredAuthToken = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return '';
  }

  try {
    const raw = window.localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    return raw ? String(raw) : '';
  } catch (_) {
    return '';
  }
};

const persistAuthToken = (token) => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }

  try {
    if (!token) {
      window.localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
      return;
    }

    window.localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token);
  } catch (_) {
    // ignore storage errors
  }
};

export const getAuthToken = () => getStoredAuthToken();
export const setAuthToken = (token) => {
  persistAuthToken(token);
  return token || '';
};
export const clearAuthToken = () => persistAuthToken('');

const buildUrl = (base, endpoint) => {
  if (/^https?:\/\//i.test(endpoint)) {
    return endpoint;
  }

  if (!base) {
    return endpoint;
  }

  const trimmedBase = base.trim();
  if (!trimmedBase) {
    return endpoint;
  }

  const sanitizedBase = ensureTrailingSlash(trimmedBase);
  const sanitizedEndpoint = endpoint.replace(/^\/+/, '');

  const prefixMatch = sanitizedBase.match(/^([^:]+:\/\/[^/]+)(.*)$/);
  const prefix = prefixMatch ? prefixMatch[1] : '';
  const basePath = prefixMatch ? prefixMatch[2] : sanitizedBase;

  const baseSegments = basePath.replace(/\/+$/, '').split('/').filter(Boolean);
  const endpointSegments = sanitizedEndpoint.split('/').filter(Boolean);

  let overlap = 0;
  const maxOverlap = Math.min(baseSegments.length, endpointSegments.length);

  for (let i = maxOverlap; i > 0; i -= 1) {
    const baseSuffix = baseSegments.slice(-i).join('/');
    const endpointPrefix = endpointSegments.slice(0, i).join('/');
    if (baseSuffix === endpointPrefix) {
      overlap = i;
      break;
    }
  }

  const finalEndpointSegments = endpointSegments.slice(overlap);
  const finalPathSegments = [...baseSegments, ...finalEndpointSegments];
  const finalPath = finalPathSegments.join('/');

  if (prefix) {
    return finalPath ? `${prefix}/${finalPath}` : `${prefix}/`;
  }

  return finalPath || '';
};
const appendQueryParams = (endpoint, params = {}) => {
  if (!endpoint || typeof endpoint !== 'string') {
    return endpoint;
  }

  if (!params || typeof params !== 'object') {
    return endpoint;
  }

  const [path, existingQuery = ''] = endpoint.split('?');
  const searchParams = new URLSearchParams(existingQuery);

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }

    const valuesArray = Array.isArray(value) ? value : [value];
    const normalized = valuesArray
      .map((item) => (item === undefined || item === null ? '' : String(item).trim()))
      .filter((item) => item);

    if (normalized.length === 0) {
      return;
    }

    searchParams.set(key, normalized.join(';'));
  });

  const queryString = searchParams.toString();
  return queryString ? `${path}?${queryString}` : path;
};
const withDefaultOptions = (options = {}) => {
  const normalized = { ...options };
  if (!normalized.credentials) {
    normalized.credentials = 'include';
  }
  return normalized;
};
const withAuthHeaders = (headers = {}) => {
  const token = getAuthToken();
  const normalizedHeaders = headers instanceof Headers
    ? Object.fromEntries(headers.entries())
    : { ...headers };

  if (token && !normalizedHeaders.Authorization) {
    normalizedHeaders.Authorization = `Bearer ${token}`;
  }

  return normalizedHeaders;
};
async function fetchData(endpoint, options = {}) {
  const url = buildUrl(BASE_URL, endpoint);
  const response = await fetch(url, {
    ...withDefaultOptions(options),
    headers: withAuthHeaders(options.headers || {}),
  });
  if (!response.ok) {
    throw new Error(`Error fetching ${endpoint}: ${response.statusText}`);
  }

  const text = await response.text();
  try {
    return JSON.parse(text);
  } catch {
    throw new Error(`Invalid JSON response from ${url}`);
  }
}

async function fetchFromCandidates(candidates, options = {}) {
  const endpoints = Array.isArray(candidates) ? candidates : [candidates].filter(Boolean);
  if (endpoints.length === 0) {
    throw new Error('No endpoints provided to fetchFromCandidates');
  }

  const failures = [];

  for (const endpoint of endpoints) {
    try {
      return await fetchData(endpoint, options);
    } catch (error) {
      failures.push({ endpoint, error });
      if (
        process.env.NODE_ENV !== 'production' &&
        typeof console !== 'undefined' &&
        typeof console.warn === 'function'
      ) {
        console.warn(`Request to ${endpoint} failed`, error);
      }
    }
  }

  const detail = failures
    .map(({ endpoint, error }) => `${endpoint}: ${error.message}`)
    .join(' | ');
  const aggregatedError = new Error(`All endpoint requests failed (${detail})`);
  aggregatedError.failures = failures;
  throw aggregatedError;
}
const sanitizeChecklistId = (value) => {
  if (value === undefined || value === null) {
    return '';
  }

  const stringValue = String(value).trim();
  if (!stringValue) {
    return '';
  }

  const cleaned = stringValue
    .replace(/['\"`]/g, '')
    .replace(/[{}()[\]]/g, ' ')
    .replace(/\bfk[_-]?checklist\b\s*[:=]?/gi, ' ')
    .replace(/\bchecklist[_-]?id\b\s*[:=]?/gi, ' ')
    .replace(/\bid[_-]?checklist\b\s*[:=]?/gi, ' ')
    .replace(/\bchecklist\b\s*[:=]?/gi, ' ')
    .trim();

  if (!cleaned) {
    return '';
  }

  const parts = cleaned
    .split(/[;|,\/\s]+/)
    .map((part) => part.trim())
    .filter(Boolean)
    .filter((part) => !/^(null|undefined|ninguno|ninguna)$/i.test(part));

  if (parts.length === 0) {
    return '';
  }

  return parts[0];
};


const splitChecklistIdentifiers = (value) => {
  if (value === undefined || value === null) {
    return [];
  }

  if (Array.isArray(value)) {
    return value.flatMap((item) => splitChecklistIdentifiers(item));
  }

  if (typeof value === 'object') {
    const candidates = [
      value.id,
      value.ID,
      value.checklist_id,
      value.fk_checklist,
      value.id_checklist,
      value.guid,
      value.uuid,
    ];

    return candidates.flatMap((candidate) => splitChecklistIdentifiers(candidate));
  }

  return String(value)
    .split(/[;|,\\/\s]+/)
    .map((part) => part.trim())
    .filter(Boolean);
};

const buildChecklistIdKey = (value) => {
  const normalized = sanitizeChecklistId(value);
  return normalized ? normalized.toLowerCase() : '';
};

const isObjectLike = (value) => typeof value === 'object' && value !== null;

const extractChecklistEntries = (payload, filterKeys = null) => {
  if (payload === undefined || payload === null) {
    return [];
  }

  if (Array.isArray(payload)) {
    return payload.flatMap((item) => extractChecklistEntries(item, filterKeys));
  }

  if (!isObjectLike(payload)) {
    return [];
  }

  const candidateId =
    payload.id ??
    payload.ID ??
    payload.checklist_id ??
    payload.fk_checklist ??
    payload.id_checklist ??
    payload.guid ??
    payload.uuid;

  const normalizedId = sanitizeChecklistId(candidateId);
  const idKey = buildChecklistIdKey(normalizedId);
  const hasIdMatch = idKey && filterKeys ? filterKeys.has(idKey) : true;

  const candidateName =
    payload.nombre ??
    payload.name ??
    payload.titulo ??
    payload.descripcion ??
    payload.label ??
    payload.nombre_checklist ??
    payload.checklist_nombre;

  const normalizedEntry = { ...payload };

  if (normalizedId) {
    normalizedEntry.id = normalizedId;
  }

  if (candidateName && !normalizedEntry.nombre) {
    normalizedEntry.nombre = String(candidateName);
  }

  const shouldInclude = Boolean(normalizedId || candidateName);

  const nestedValues = Object.values(payload).filter((value) => isObjectLike(value));
  const nestedEntries = nestedValues.flatMap((value) => extractChecklistEntries(value, filterKeys));

  if (filterKeys) {
    if (hasIdMatch && normalizedId) {
      return [normalizedEntry];
    }
    return nestedEntries;
  }

  if (shouldInclude) {
    return [normalizedEntry, ...nestedEntries];
  }

  return nestedEntries;
};

const dedupeChecklistEntries = (entries) => {
  const seen = new Set();

  return entries.filter((entry) => {
    if (!isObjectLike(entry)) {
      return false;
    }

    const idKey = buildChecklistIdKey(entry.id ?? entry.ID ?? entry.checklist_id ?? entry.fk_checklist);
    const nameKey = entry.nombre ?? entry.name ?? entry.titulo ?? entry.descripcion ?? entry.label;
    const key = idKey ? `id:${idKey}` : nameKey ? `name:${String(nameKey).toLowerCase()}` : JSON.stringify(entry);

    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};
const TAREAS_REGISTROS_WITH_CHECKLIST_QUERY = appendQueryParams('/api/tareas-registros.php', { get_checklist_data: 1 });
const ENDPOINT_CANDIDATES = {
  activos: [
    'api-simple-all.php?table=activos&action=list',
    'activos-completo.php',
    'activos.php',
    'api-simple-all.php?table=activos',
  ],
  categoriaActivos: [
    'categoria-activos.php',
    'api-simple-all.php?table=categoria-activos',
  ],
  categoriaProfesional: [
    'categoria-profesional.php',
    'api-simple-all.php?table=categoria-profesional',
  ],
  categoriaProfesionalGrupos: [
    'categoria-profesional-completo.php',
    'categoria-profesional.php',
    'api-simple-all.php?table=categoria-profesional-grupos&action=list',
  ],
  categorias: [
    'categorias.php',
    'categorias-simple.php',
    'api-simple-all.php?table=categorias',
  ],
  equipos: [
    'equipos.php',
    'api-simple-all.php?table=equipos',
  ],
  planes: [
    'planes.php',
    'api-simple-all.php?table=planes',
  ],
  tareasRegistros: [
    '/api/tareas-registros.php',
    'tareas-registros.php',
  ],
  tipologiasCentro: [
    'tipologias-centro.php',
    'api-simple-all.php?table=tipologias-centro',
  ],
    instancias: [
    'instancias.php',
    'instancias-simple.php',
    'api-simple-all.php?table=instancias',
  ],
};

const TAREAS_REGISTROS_CHECKLIST_ENDPOINTS = [
  TAREAS_REGISTROS_WITH_CHECKLIST_QUERY,
  appendQueryParams('tareas-registros.php', { get_checklist_data: 1 }),
  'tareas-registros-checklists.php',
];

export const fetchCategorias = () => fetchFromCandidates(ENDPOINT_CANDIDATES.categorias);
export const fetchTareasRegistros = () => fetchFromCandidates(ENDPOINT_CANDIDATES.tareasRegistros);
export const fetchTareasRegistrosWithChecklist = () =>
  fetchFromCandidates(TAREAS_REGISTROS_CHECKLIST_ENDPOINTS);
export const fetchActivos = () => fetchFromCandidates(ENDPOINT_CANDIDATES.activos);
export const fetchCategoriaActivos = () => fetchFromCandidates(ENDPOINT_CANDIDATES.categoriaActivos);
export const fetchCategoriaProfesional = () => fetchFromCandidates(ENDPOINT_CANDIDATES.categoriaProfesional);
export const fetchCategoriaProfesionalGrupos = () =>
  fetchFromCandidates(ENDPOINT_CANDIDATES.categoriaProfesionalGrupos);
export const fetchEquipos = () => fetchFromCandidates(ENDPOINT_CANDIDATES.equipos);
export const fetchPlanes = () => fetchFromCandidates(ENDPOINT_CANDIDATES.planes);
export const fetchTipologiasCentro = () => fetchFromCandidates(ENDPOINT_CANDIDATES.tipologiasCentro);
export const fetchInstancias = () => fetchFromCandidates(ENDPOINT_CANDIDATES.instancias);

export const fetchChecklists = async (options = {}) => {
  const { ids } = options || {};

  const normalizedIds = Array.isArray(ids) ? ids : ids !== undefined && ids !== null ? [ids] : [];

  const sanitizedIds = Array.from(
    new Set(
      normalizedIds
        .flatMap((value) => splitChecklistIdentifiers(value))
        .map((value) => sanitizeChecklistId(value))
        .filter((value) => value),
    ),
  );

  const filterKeys = new Set(sanitizedIds.map((value) => buildChecklistIdKey(value)).filter(Boolean));

  const response = await fetchFromCandidates(TAREAS_REGISTROS_CHECKLIST_ENDPOINTS);
  const entries = dedupeChecklistEntries(
    extractChecklistEntries(response, filterKeys.size > 0 ? filterKeys : null),
  );

  if (entries.length > 0) {
    return entries;
  }

  if (filterKeys.size === 0) {
    return response;
  }

  const aggregatedError = new Error(
    `Unable to fetch checklist data for ids: ${sanitizedIds.join(', ')}`,
  );
  aggregatedError.failures = [{ endpoint: 'tareas-registros.php?get_checklist_data=1' }];
  throw aggregatedError;
};

export const login = async ({ username, password }) => {
  const url = buildUrl(BASE_URL, appendQueryParams('auth.php', { action: 'login' }));
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      username,
      password,
    }),
  });

  const rawText = await response.text();
  let data = {};

  try {
    data = rawText ? JSON.parse(rawText) : {};
  } catch (_) {
    throw new Error('Respuesta de login inválida');
  }

  if (!response.ok) {
    const message = data?.message || data?.error || `Login fallido (${response.status})`;
    throw new Error(message);
  }

  const token =
    data?.token ||
    data?.auth_token ||
    data?.access_token ||
    data?.data?.token ||
    data?.data?.auth_token ||
    data?.data?.access_token ||
    '';
  const user = data?.user || data?.usuario || data?.data?.user || null;

  if (token) {
    setAuthToken(token);
  }

  return {
    token,
    user,
    raw: data,
  };
};

export const createUser = async ({ username, password, role, is_active }) => {
  const url = buildUrl(BASE_URL, 'users.php');
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: withAuthHeaders({
      'Content-Type': 'application/json',
    }),
    body: JSON.stringify({
      username,
      password,
      role,
      is_active,
    }),
  });

  const rawText = await response.text();
  let data = {};
  try {
    data = rawText ? JSON.parse(rawText) : {};
  } catch (_) {
    throw new Error('Respuesta de creación inválida');
  }

  if (!response.ok) {
    const message = data?.message || data?.error || `Error creando usuario (${response.status})`;
    throw new Error(message);
  }

  return data;
};
